// apps/client/src/hooks/pipelineTracker/useTasksReport.ts
//
// Pipeline Tracker: per-person task compliance report.
//
// The wire types below mirror the payload built by
// services/client-functions/src/pipelineTracker/reports/tasksReport.ts. They are declared here
// rather than imported because the browser bundle cannot reach into the functions package, and
// because the Pipeline Tracker is fenced off from the rest of the product by CI
// (.github/scripts/check-pipeline-tracker-isolation.sh): a shared package would put a tracker
// concern into code the LMG surface also consumes.
//
// WHAT THIS REPORT IS NOT: it is not restricted to deals present in the Performance snapshot.
// Performance is a filtered export and Tasks is not, so most task rows have no matching deal by
// design. If a figure here ever looks smaller than the raw export, that is the first thing to
// check, and the backend test suite asserts the figures do not move when a deal allow-list is
// applied.
//
// NO PII. The task table carries no borrower name, no deal name and no comments column, so
// nothing rendered from this payload can leak one.

import { useMemo } from 'react';
import { REPORT_CALLABLE, TASKS_REPORT_ACTION } from './reportContract';
import {
  fetchTrackerCall,
  peekTrackerCall,
  prefetchTrackerCall,
  useTrackerCall,
  type TrackerRequest,
} from './reportCache';
export { REPORT_CALLABLE, TASKS_REPORT_ACTION } from './reportContract';

/**
 * The tracker report dispatcher, and the action within it.
 *
 * Declared as constants so the callable name lives in exactly one place: the dispatcher is owned
 * elsewhere, and a silent rename would surface as a generic "internal" error with no clue where
 * it came from.
 */

export type TaskState = 'todo' | 'doing' | 'done' | 'unclassified';

export type TaskOwnerRole = 'broker' | 'back_office';

/**
 * Normalised task priority. Salestrekker's values are Low / Medium / High; 'not_set' is a blank,
 * NULL or unrecognised value and is NEVER read as Low. MIRRORS TaskPriority in the backend pure
 * module; both halves change together.
 */
export type TaskPriority = 'low' | 'medium' | 'high' | 'not_set';

export interface TaskAssigneeFigures {
  assignee: string;
  isUnassigned: boolean;
  /** Broker or back office, from the broker roster. Absent when owners could not be split. */
  ownerRole?: TaskOwnerRole;
  toDo: number;
  doing: number;
  done: number;
  unclassified: number;
  totalTasks: number;
  totalDue: number;
  outstanding: number;
  totalOverdue: number;
  /**
   * Of the overdue tasks, the ones whose priority is High (same overdue rule as totalOverdue).
   * Optional so a payload from before priority was read renders without the column rather than
   * with a zero that would claim nothing urgent is late.
   */
  overdueHigh?: number;
  /**
   * The outstanding split (V6): open and due from the as-at date to the window end, and open but
   * not due by then (or undated). notDue + due + totalOverdue = outstanding. Optional so a payload
   * from before the split renders without the two columns rather than with blanks.
   */
  due?: number;
  notDue?: number;
  doneInWindow: number;
  totalDueInWindow: number;
}

export interface TaskDrilldownRow {
  taskId: string;
  dealRef: string | null;
  assignee: string;
  taskGroup: string;
  dueDate: string | null;
  /** Whole days from due date to the as-at date. Positive is overdue. */
  ageDays: number | null;
  state: TaskState;
  /** Normalised priority. Optional for the same reason as overdueHigh. */
  priority?: TaskPriority;
}

export interface UnmappedTaskName {
  raw: string;
  workflow: string;
  count: number;
  normalisedRaw: string;
  reason: string;
  danglingCanonicalKey?: string;
}

export interface TasksReportProvenance {
  asAtDate: string;
  snapshotUploadId: string;
  window: { start: string; end: string };
  taxonomyIsSeed: boolean;
  taxonomyInvalidReason?: string;
  scopeNote: string;
  unclassifiedNote: string;
  ownerClassification?:
    | { status: 'classified'; performanceAsAtDate: string; rosterIsSeed: boolean; note: string }
    | { status: 'unavailable'; reason: string };
}

export interface TasksReportPayload {
  assignees: TaskAssigneeFigures[];
  totals: TaskAssigneeFigures;
  /** Broker and back-office subtotals; they add up to `totals`. Present only when classified. */
  ownerSubtotals?: { broker: TaskAssigneeFigures; backOffice: TaskAssigneeFigures };
  unmappedTaskNames: UnmappedTaskName[];
  taskNameCoverage: { mappedTasks: number; unmappedTasks: number; coveragePct: number };
  provenance: TasksReportProvenance;
  drilldown?: TaskDrilldownRow[];
  drilldownTotal?: number;
}

export type TasksReportResponse =
  | ({ status: 'ok' } & TasksReportPayload)
  | { status: 'no_snapshot'; asAtDate: string | null; reason: 'never_ingested' | 'not_committed' }
  | { status: 'invalid_request'; reason: string };

/**
 * `% completed`, with an explicit zero denominator.
 *
 * MIRRORS completionRatio / formatCompletionPct in the backend pure module. The copy is
 * deliberate and both halves must change together.
 *
 * The customer's own spreadsheet gets this wrong twice: the column is filled on only 57 percent
 * of rows because it divides by zero, and where it is filled it shows values like
 * 9.999999999999999 because it renders a raw binary float. A blank cell reads as "completed
 * nothing", which is a very different statement from "nothing was due".
 */
export function completionRatio(doneInWindow: number, totalDueInWindow: number): number | null {
  if (!Number.isFinite(doneInWindow) || !Number.isFinite(totalDueInWindow)) return null;
  if (totalDueInWindow <= 0) return null;
  return doneInWindow / totalDueInWindow;
}

export function formatCompletionPct(ratio: number | null): string {
  if (ratio === null) return 'No tasks due';
  return `${Math.round(ratio * 1000) / 10}%`;
}

export interface UseTasksReportParams {
  companyId: string;
  /** Snapshot to read. Omitted means the most recently committed snapshot. */
  asAtDate?: string;
  windowStart?: string;
  windowEnd?: string;
}

export interface UseTasksReportReturn {
  response: TasksReportResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Load the per-person task compliance report for one committed snapshot.
 *
 * `no_snapshot` is returned as DATA, not as an error: a tenant who has the feature but has not
 * uploaded anything yet is in a normal first-run state, and an error banner there would read like
 * a fault the operator has to report.
 */
export function useTasksReport({
  companyId,
  asAtDate,
  windowStart,
  windowEnd,
}: UseTasksReportParams): UseTasksReportReturn {
  // Absent bounds are omitted rather than sent as `undefined`: the callable transport serialises
  // an undefined field as JSON null, which the server's ISO-date check rejects.
  const request = useMemo(
    () =>
      companyId
        ? {
            action: TASKS_REPORT_ACTION,
            companyId,
            ...(asAtDate ? { asAtDate } : {}),
            ...(windowStart ? { windowStart } : {}),
            ...(windowEnd ? { windowEnd } : {}),
          }
        : null,
    [companyId, asAtDate, windowStart, windowEnd]
  );
  const { data, loading, error, refetch } = useTrackerCall<TasksReportResponse>(
    REPORT_CALLABLE,
    request,
    {
      label: 'useTasksReport',
      // Surfaced, never swallowed. A silent empty table here reads as "nobody has any tasks",
      // which is the most misleading thing this report could say.
      describeError: (err) =>
        err instanceof Error ? err.message : 'Failed to load the task report',
    }
  );

  return { response: error ? null : data, loading, error, refetch };
}

export interface FetchTaskDrilldownParams {
  companyId: string;
  assignee: string;
  asAtDate?: string;
  windowStart?: string;
  windowEnd?: string;
  limit?: number;
  /**
   * Narrow the list to one priority. Omitted, the request carries no priority field at all, so
   * the request, the cache key and the server's answer are exactly as before the field existed.
   */
  priority?: TaskPriority;
}

/** The drill-down request, built in one place so a prefetch, a peek and a fetch share a cache entry. */
export function drilldownRequest({
  companyId,
  assignee,
  asAtDate,
  windowStart,
  windowEnd,
  limit,
  priority,
}: FetchTaskDrilldownParams): TrackerRequest {
  return {
    action: TASKS_REPORT_ACTION,
    companyId,
    drilldownAssignee: assignee,
    ...(asAtDate ? { asAtDate } : {}),
    ...(windowStart ? { windowStart } : {}),
    ...(windowEnd ? { windowEnd } : {}),
    ...(limit ? { drilldownLimit: limit } : {}),
    ...(priority ? { drilldownPriority: priority } : {}),
  };
}

function toDrilldown(payload: TasksReportResponse): { rows: TaskDrilldownRow[]; total: number } {
  if (payload.status !== 'ok') return { rows: [], total: 0 };
  return { rows: payload.drilldown ?? [], total: payload.drilldownTotal ?? 0 };
}

/**
 * One person's task list, bounded by the server.
 *
 * A plain async function rather than a hook, because the drill-down opens on a click and closes
 * again: modelling that as an effect chain adds a render cycle and a stale-response race for no
 * benefit. The caller owns the open/closed state.
 *
 * Cached like the report (reportCache.ts): reopening the same person's drawer does not ask the
 * server again, and invalidateTrackerReports() drops it with every other tracker answer.
 */
export async function fetchTaskDrilldown(
  params: FetchTaskDrilldownParams
): Promise<{ rows: TaskDrilldownRow[]; total: number }> {
  return toDrilldown(await fetchTrackerCall<TasksReportResponse>(REPORT_CALLABLE, drilldownRequest(params)));
}

/**
 * The cached task list for a person, if one is held, so the drawer can draw it on the frame it
 * opens rather than after a skeleton. An aged answer is still returned; fetchTaskDrilldown
 * re-asks for it.
 */
export function peekTaskDrilldown(
  params: FetchTaskDrilldownParams
): { rows: TaskDrilldownRow[]; total: number } | undefined {
  const hit = peekTrackerCall<TasksReportResponse>(REPORT_CALLABLE, drilldownRequest(params));
  return hit ? toDrilldown(hit.data) : undefined;
}

/**
 * Warm one person's task list (the row the reader is pointing at), so a click usually finds it
 * ready. Failures are left for the drawer to meet and report.
 */
export function prefetchTaskDrilldown(params: FetchTaskDrilldownParams): void {
  prefetchTrackerCall(REPORT_CALLABLE, drilldownRequest(params));
}
